/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { isNonceEnabled, SDK_FRAGMENT_NAME } from '@msdyn365-commerce/core-internal';
import * as React from 'react';
import { IUsercentricsInlineScriptProps } from './usercentrics-inline-script.props.autogenerated';

import { IInternalTelemetry, LogLevel } from '@msdyn365-commerce/telemetry-internal';

const logDebug = (debugMessage: string, telemetry: IInternalTelemetry | undefined): void => {
    telemetry && telemetry.log(LogLevel.Debug, debugMessage);
};

const logError = (errorMessage: string, telemetry: IInternalTelemetry | undefined): void => {
    telemetry && telemetry.log(LogLevel.Error, errorMessage);
};

/**
 * Returns true if the given string matches an URL pattern
 *
 * @param source The string to check against
 */
const isAbsoluteUrl = (source: string): boolean => {
    return /^(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?$/.test(source);
};

const isRelativeUrl = (source: string): boolean => {
    return /^(?!www\.|(?:http|ftp)s?:\/\/|[A-Za-z]:\\|\/\/).*(\.js){1}$/.test(source);
};

/**
 *
 * UsercentricsScript
 * @extends {React.PureComponent<IUsercentricsScriptProps<{}>>}
 */
class UsercentricsInlineScript extends React.PureComponent<IUsercentricsInlineScriptProps<{}>> {
    constructor(props: IUsercentricsInlineScriptProps<{}>) {
        super(props);
    }

    public render(): JSX.Element | null {
        const { config } = this.props;
        logDebug(`Adding script tags for '${this.props.id}/${this.props.typeName}'`, this.props.telemetry);
        const source = config.inlineScript;

        if (!source || source === '' || isAbsoluteUrl(source) || isRelativeUrl(source)) {
            logError(
                'Invalid inline script - Empty inline source defined or a url is being used as a source. Use usercentrics-external-script to load scripts from an external or relative url.',
                this.props.telemetry
            );
            return null;
        }

        if (source.includes('</script>')) {
            logError('Invalid inline script - inline script should not contain html tags', this.props.telemetry);
            return null;
        }

        const nonceToken = this.props.context && isNonceEnabled(this.props.context.request);
        const scriptHtml = `<script type="text/plain" data-usercentrics="${this.props.config.dataProcessingServiceName}" ${
            nonceToken ? `nonce='${nonceToken}'` : ''
        }>${source}</script>`;
        return React.createElement(SDK_FRAGMENT_NAME, { key: this.props.id, dangerouslySetInnerHTML: { __html: scriptHtml } });
    }
}

export default UsercentricsInlineScript;
